<template>
  <ClientOnly>
    <main :class="['flex flex-col h-full', { 'modal-styled': isTeaserDeckWithinIframe }]">
      <ModalsAuthModals
        :has-auth-error="hasAuthError"
        :has-permissions-error="hasPermissionsError"
        :hide-close="true"
        :user="user"
        project="this content"
        content="Deck"
      />

      <template v-if="embeddedDeckLoading">
        <DeckLoading v-if="isInIframe()">
          <LoadingSpinner class="mix-blend-difference" size="sm" :has-background="false" />
        </DeckLoading>
        <LoadingSpinner v-else class="mix-blend-difference" size="sm" :has-background="false" />
      </template>

      <template v-else>
        <DeckTopBar v-if="isTeaserDeckWithinIframe" @close="closeLightbox" />

        <Deck
          v-if="deck && deckRenderState.displayDeck"
          :key="deck.id"
          data-testid="classic-deck"
          :analytics-event-name="analyticsEventNameForDeck"
          :deck="deck"
          class="flex-grow w-screen"
          :embed-deck="embeddedDeck || ({ embed_type: EmbedType.Classic } as DeepReadonly<DeckWithEmbed>)"
          :current-slide-index="currentSlideIndex"
          :slide-refresh-key="slideRefreshKey"
          :first-valid-slide="firstValidSlide"
          :last-valid-slide="lastValidSlide"
          :analytics-have-fired="analyticsHaveFired"
          :uuid="uuid?.toString()"
          :user="{} as DeepReadonly<User>"
          :site="site"
          :three-d-viewer="threeDViewer"
          @goto:hotspot="setSelectedHotspot"
          @set-current-slide-index="setCurrentSlideIndex"
          @navigation-previous-slide="previousSlide"
          @navigation-next-slide="nextSlide"
          @update:annotation-subscriber="$event.setup({})"
          @analytics-fired="handleAnalyticsFired"
          @dismiss-embed-overlay="canLoad = true"
        />

        <DeckTeaserDeck
          v-else-if="embeddedDeck && deckRenderState.displayTeaserDeck"
          class="flex-grow"
          :teaser-slides="embeddedDeck.slides"
          :deck="embeddedDeck"
          :uuid="`${uuid}`"
          :analytics-have-fired="analyticsHaveFired"
          :analytics-namespace="analyticsEventNameForDeck"
          data-testid="teaser-deck"
          :stay-in-frame="embeddedDeck.embed_type === EmbedType.Partner"
          :three-d-viewer="threeDViewer"
          @set-active-scene="threeDViewer.setActiveScene"
          @open-full-deck="handleOpenFullDeck"
          @analytics-fired="handleAnalyticsFired"
        />

        <DeckLoading v-if="deckLoading">
          <LoadingSpinner class="mix-blend-difference" size="sm" :has-background="false" />
        </DeckLoading>
      </template>
    </main>
  </ClientOnly>
</template>

<script lang="ts" setup>
import { UseEmbeddedDecksAuthError, UseEmbeddedDecksPermissionsError, UseEmbeddedDecksNotFoundError } from '@/composables/useEmbeddedDecks'
import { UseDeckAuthError, UseDeckPasswordRequiredError, UseDeckPermissionsError } from '@/composables/useDeck'
import { isInIframe } from '~/lib/utils'
import { EmbedType, type DeckWithEmbed } from '@/entities/embed'
import type { DeepReadonly } from 'vue'
import type { User } from '~/entities/user'

type AnalyticsEvent = 'teaserPageLoad' | 'teaserClickViewPresentation' | 'deckPageLoad'

definePageMeta({
  layout: 'isolated-page',
})

const { state: user } = useUser()
const globalFetchHeaders = useState<{ [key: string]: string }>('global-fetch-headers', () => ({}))
const route = useRoute()
const router = useRouter()
const deckId = ref()
const uuid = route.query?.auth ?? null
const canLoad = ref(false)
const initialSlideId = ref<number | null>(route.query?.slide ? parseInt(<string>route.query?.slide, 10) : null)
const hasAuthError = computed(
  () => embeddedDeckError.value instanceof UseEmbeddedDecksAuthError || deckError.value instanceof UseDeckAuthError,
)
const hasPermissionsError = computed(
  () =>
    embeddedDeckError.value instanceof UseEmbeddedDecksPermissionsError ||
    deckError.value instanceof UseDeckPermissionsError ||
    deckError.value instanceof UseDeckPasswordRequiredError,
)
const analyticsEventNameForDeck = computed(() => {
  if (embeddedDeck.value?.embed_type === EmbedType.Partner) {
    return 'PartnerEmbed'
  }

  if (embeddedDeck.value?.embed_type === EmbedType.Animated) {
    return 'AnimatedEmbedDeck'
  }

  return 'EmbedDeck'
})

// TODO: we should probably have a conversation on where tracking of analytics events being fired should go for embeds
const analyticsHaveFired = reactive<Record<AnalyticsEvent, boolean>>({
  teaserPageLoad: false,
  teaserClickViewPresentation: false,
  deckPageLoad: false,
})

const { $analytics, $sentry } = useNuxtApp()

const handleAnalyticsFired = (which: AnalyticsEvent) => {
  analyticsHaveFired[which] = true
}

const {
  loading: embeddedDeckLoading,
  error: embeddedDeckError,
  deck: embeddedDeck,
  isAnimatedDeck,
  deckRenderState,
  closeLightbox: _closeLightbox,
  openLightbox,
  showDeck,
} = useEmbeddedDecks(route.params.id as string)

const {
  loading: deckLoading,
  error: deckError,
  deck,
  currentSlideIndex,
  slideRefreshKey,
  site,
  setSelectedHotspot,
  updateSlideRefreshKey,
  setCurrentSlideIndex,
  previousSlide,
  nextSlide,
  firstValidSlide,
  lastValidSlide,
} = useDeck({
  id: deckId,
  user: ref(user.value.user),
  initialSlideId,
  include: ['company', 'site_image', 'model_camera'],
})

const { toggleFullscreen, isFullscreenEnabled, isCurrentlyFullscreen } = useFullscreenHelpers({ deck })

const isTeaserDeckWithinIframe = computed(() => isInIframe() && deckRenderState.value.displayDeck && isAnimatedDeck.value)

function closeLightbox() {
  if (isFullscreenEnabled.value && isCurrentlyFullscreen.value) {
    toggleFullscreen()
  }

  _closeLightbox()
}

const { priorityCameras } = useEmbedPriorityCameras({
  embedId: `${route.params.id}`,
  deckId: computed(() => deck.value?.id),
  isAnimatedDeck,
})

const threeDViewer = useThreeDViewer({ priorityCameras, canLoad, maxAge: 15 * 60 * 1000, skipHiddenNodes: true })

useDeckSetActiveThreeDScene({
  slides: computed(() => deck.value?.slides),
  currentSlideIndex,
  slideRefreshKey,
  setActiveScene: threeDViewer.setActiveScene,
})

const handleOpenFullDeck = () => {
  // Clear scene so that when the deck loads but before the new scene has been set, it's not holding onto the previous scene
  threeDViewer.clearActiveScene()
  threeDViewer.world.resetResources()

  openLightbox()

  updateSlideRefreshKey()
  deckId.value = embeddedDeck.value?.id
}

watch(
  () => embeddedDeckError.value || deckError.value,
  e => {
    if (e) {
      if (e instanceof UseEmbeddedDecksAuthError || e instanceof UseEmbeddedDecksPermissionsError || e instanceof UseDeckPermissionsError) {
        return
      }

      if (e instanceof UseEmbeddedDecksNotFoundError) {
        deckId.value = route.params.id
        return
      }

      $sentry?.captureException(e)

      router.push({ name: 'index' })
    }
  },
)

watchEffect(() => {
  globalFetchHeaders.value['X-Embed-Referring-URL'] = document.referrer
  globalFetchHeaders.value['X-Embedded-Content'] = 'deck'
  globalFetchHeaders.value['X-Deck-Id'] = `${embeddedDeck.value?.id || deck.value?.id || null}`
})

onMounted(() => {
  if (route.query.mode === 'preview') {
    // @ts-ignore - null the track method since we don't want analytics sent for previews
    $analytics.track = () => {}
  }
})

onUnmounted(() => {
  delete globalFetchHeaders.value['X-Embed-Referring-URL']
  delete globalFetchHeaders.value['X-Embedded-Content']
  delete globalFetchHeaders.value['X-Deck-Id']
})

watch(
  () => embeddedDeck.value?.id,
  td => {
    if (!td) return

    if (embeddedDeck.value?.embed_type === EmbedType.Classic) {
      deckId.value = td
    } else {
      canLoad.value = true
    }
  },
)

watch(
  () => deck.value?.id,
  dId => {
    if (!dId) return

    showDeck('displayDeck')
  },
  { immediate: true },
)
</script>
